import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { pricingSimulatorUrls } from 'config/urls';
import _ from 'lodash';
import { errorMessage } from 'config/constants';

interface Props {
    queryKey: any[];
    variables?: any;
    queryConfig?: {
        enabled?: boolean;
    };
    offset: number;
    tempAllCheckedAttribute: any;
    setTempAllCheckedAttribute: Function;
    tempAllSelectedValueForAllAttribute: any;
    selectedFilter: any;
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
    count: number;
}

function useFetchFilterAttributes({
    queryKey,
    variables = {},
    queryConfig = {},
    offset,
    tempAllCheckedAttribute,
    setTempAllCheckedAttribute,
    tempAllSelectedValueForAllAttribute,
    selectedFilter,
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState<any | null>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            if (!offset) {
                setData(null);
                setCount(0);
            }
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method: 'post',
                    url: pricingSimulatorUrls.getFilterAttributes,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        // if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            let parsedData = [];
            if (offset) {
                parsedData = payload?.attribute_names?.map((name: string) => {
                    return {
                        label: name,
                        value: name,
                    };
                });
                // updating attribute property if select all has been checked
                if (tempAllSelectedValueForAllAttribute[selectedFilter?.value]) {
                    const newTempAllCheckedAttribute = _.cloneDeep(tempAllCheckedAttribute);
                    parsedData.forEach((item: any) => newTempAllCheckedAttribute[selectedFilter.value].add(item.value));
                    setTempAllCheckedAttribute(newTempAllCheckedAttribute);
                }
                setData([...data, ...parsedData]);
            } else {
                const parsedData: any = payload?.attribute_names?.map((name: string) => {
                    return {
                        label: name,
                        value: name,
                    };
                });

                setCount(payload.total_count || 0);
                setData(parsedData);
            }
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData([]);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export default useFetchFilterAttributes;
